import { httpClient } from './http/httpClient.js';

const API_URL = '/api/forms';
const FORM_RESPONSES_API_URL = `${API_URL}/responses`;

const buildQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    searchParams.append(key, String(value));
  });

  return searchParams.toString();
};

const buildFormsUrl = (params = {}) => {
  const queryString = buildQueryString(params);
  return queryString ? `${API_URL}?${queryString}` : API_URL;
};

const getCollection = (payload, keys = []) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  const fallbackKeys = ['data', 'items', 'records', 'responses', 'forms'];

  for (const key of [...keys, ...fallbackKeys]) {
    if (Array.isArray(payload?.[key])) {
      return payload[key];
    }
  }

  return [];
};

const ensureSuccessPayload = (payload, fallbackMessage) => {
  if (payload && typeof payload === 'object' && payload.success === false) {
    throw new Error(payload.message || fallbackMessage);
  }

  return payload;
};

export const formService = {
  getForms: async (params = {}) => {
    const payload = ensureSuccessPayload(
      await httpClient.get(buildFormsUrl(params)),
      'No se pudieron obtener los formularios.'
    );

    return {
      responses: getCollection(payload, ['responses']),
      pagination: payload?.pagination || null,
      payload
    };
  },

  getRequiredFieldsConfig: async () => {
    const data = await httpClient.get(`${API_URL}/config/required-fields`);
    if (!data.success) throw new Error(data.message || 'Error obteniendo campos requeridos');
    return data.data;
  },

  getFormsByTaskType: async (taskTypeId, type = null) => {
    const queryString = buildQueryString(type ? { type } : {});
    const url = queryString
      ? `${API_URL}/by-task-type/${encodeURIComponent(taskTypeId)}?${queryString}`
      : `${API_URL}/by-task-type/${encodeURIComponent(taskTypeId)}`;

    const payload = ensureSuccessPayload(
      await httpClient.get(url),
      'No se pudo cargar el formulario asociado.'
    );

    return getCollection(payload, ['forms', 'data']);
  },

  getFormResponses: async (params = {}) => {
    const queryString = buildQueryString(params);
    const url = queryString ? `${FORM_RESPONSES_API_URL}?${queryString}` : FORM_RESPONSES_API_URL;
    const payload = ensureSuccessPayload(
      await httpClient.get(url),
      'No se pudieron obtener las respuestas de formularios.'
    );

    return {
      responses: getCollection(payload, ['responses', 'data']),
      pagination: payload?.pagination || null,
      payload
    };
  },

  exportFormResponses: async (params = {}) => {
    const queryString = buildQueryString(params);
    const url = queryString ? `${FORM_RESPONSES_API_URL}/export?${queryString}` : `${FORM_RESPONSES_API_URL}/export`;
    return httpClient.get(url, { parseAs: 'blob' });
  },

  getFormResponseFields: async () => {
    const payload = ensureSuccessPayload(
      await httpClient.get(`${FORM_RESPONSES_API_URL}/fields`),
      'No se pudieron obtener los campos de las respuestas.'
    );

    return getCollection(payload, ['fields']);
  },

  updateFormResponse: async (formResponseId, formResponseData) => {
    const payload = await httpClient.put(`${FORM_RESPONSES_API_URL}/${encodeURIComponent(formResponseId)}`, {
      data: formResponseData
    });
    return ensureSuccessPayload(payload, 'No se pudo actualizar la respuesta.');
  },

  deleteFormResponse: async (formResponseId) => {
    const payload = await httpClient.delete(`${FORM_RESPONSES_API_URL}/${encodeURIComponent(formResponseId)}`);
    return ensureSuccessPayload(payload, 'No se pudo eliminar la respuesta.');
  },

  createForm: async (formData) => {
    return httpClient.post(API_URL, { data: formData });
  },

  updateForm: async (id, formData) => {
    return httpClient.put(`${API_URL}/${id}`, { data: formData });
  },

  deleteForm: async (id) => {
    await httpClient.delete(`${API_URL}/${id}`);
    return true;
  },

  duplicateForm: async (id, name) => {
    return httpClient.post(`${API_URL}/${id}/duplicate`, {
      data: { name }
    });
  }
};
